import React from 'react';

export interface GradedSlabProps {
  imageUrl: string;
  cardName: string;
  setName: string;
  number: string;
  gradingCompany?: string;
  gradingGrade?: string;
  aspectRatio?: string;
  height?: string | number;
  rarity?: string;
}

export function parseGradingInfo(condition?: string, notes?: string) {
  if (!condition) return null;
  const cleanCond = condition.trim();
  const regex = /^(PSA|BGS|CGC|SGC)\s*(\d+(\.\d+)?)$/i;
  const match = cleanCond.match(regex);
  if (match) return { company: match[1].toUpperCase(), grade: match[2] };
  const companyMatch = cleanCond.match(/(PSA|BGS|CGC|SGC)/i);
  if (companyMatch) {
    const company = companyMatch[1].toUpperCase();
    const numberMatch = cleanCond.match(/\d+(\.\d+)?/);
    return { company, grade: numberMatch ? numberMatch[0] : '10' };
  }
  if (notes) {
    const notesMatch = notes.trim().match(regex);
    if (notesMatch) return { company: notesMatch[1].toUpperCase(), grade: notesMatch[2] };
    const notesCompanyMatch = notes.match(/(PSA|BGS|CGC|SGC)/i);
    if (notesCompanyMatch) {
      const company = notesCompanyMatch[1].toUpperCase();
      const numberMatch = notes.match(/\d+(\.\d+)?/);
      return { company, grade: numberMatch ? numberMatch[0] : '10' };
    }
  }
  return null;
}

function getGradeDescription(company: string, gradeStr: string): string {
  const g = parseFloat(gradeStr);
  if (isNaN(g)) return 'GRADED';
  if (g === 10)  return company === 'PSA' ? 'GEM MT' : 'PRISTINE';
  if (g === 9.5) return 'GEM MT';
  if (g === 9)   return 'MINT';
  if (g === 8.5) return 'NM-MT+';
  if (g === 8)   return 'NM-MT';
  if (g === 7.5) return 'NM+';
  if (g === 7)   return 'NM';
  if (g >= 6)    return 'EX-MT';
  if (g >= 5)    return 'EX';
  if (g >= 4)    return 'VG-EX';
  if (g >= 3)    return 'VG';
  if (g >= 2)    return 'GOOD';
  return 'POOR';
}

function getCertNumber(company: string, cardName: string, number: string): string {
  let hash = 0;
  const seed = `${company}-${cardName}-${number}`;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  const raw = Math.abs(hash).toString();
  if (company === 'CGC') return `${raw.slice(0,7).padEnd(7,'0')}-${(Math.abs(hash)%100).toString().padStart(3,'0')}`;
  return raw.slice(0, 8).padEnd(8, '0');
}

function getBeckettSubgrades(gradeVal: number, seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  if (gradeVal === 10 && Math.abs(hash) % 2 === 0) return { centering:'10', corners:'10', edges:'10', surface:'10' };
  const minSub = Math.max(1, gradeVal - 0.5);
  let c = gradeVal, co = gradeVal, e = gradeVal, s = gradeVal;
  const bump = (v: number) => v + 0.5 <= 10 ? v + 0.5 : v;
  const r = Math.abs(hash) % 100;
  if (r < 25) { c = minSub; co = bump(gradeVal); }
  else if (r < 50) { co = minSub; e = bump(gradeVal); }
  else if (r < 75) { e = minSub; s = bump(gradeVal); }
  else { s = minSub; c = bump(gradeVal); }
  const clamp = (v: number) => Math.max(1, Math.min(10, Math.round(v*2)/2));
  return { centering:clamp(c).toFixed(1), corners:clamp(co).toFixed(1), edges:clamp(e).toFixed(1), surface:clamp(s).toFixed(1) };
}

// Inject once: container query styles
// The slab has aspect ratio 2.3:3.7 ≈ 0.622 wide:tall
// Label target height = 18% of slab height = 18% * (3.7/2.3) * width = ~29% of width = 29cqw
// All font sizes in cqw so they scale with container width at any render size.
const STYLE_ID = 'gs-styles-v6';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .gs-slab {
      container-type: inline-size;
      container-name: slab;
    }
    /* Label wrapper: exactly 28cqw tall = ~18% of slab height for 2.3:3.7 ratio */
    .gs-label-wrap {
      height: 28cqw;
      overflow: hidden;
      flex-shrink: 0;
    }
    .gs-label-inner {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      overflow: hidden;
    }
    /* PSA — left column text */
    .gs-psa-setname { font-size: 2.6cqw; font-weight: 700; line-height: 1; }
    .gs-psa-name    { font-size: 4.8cqw; font-weight: 900; line-height: 1; }
    .gs-psa-edition { font-size: 2.2cqw; font-weight: 600; line-height: 1; }
    .gs-psa-logotext{ font-size: 3.8cqw; font-weight: 900; line-height: 1; }
    /* PSA — right column */
    .gs-psa-cardnum { font-size: 2.6cqw; font-weight: 700; line-height: 1; }
    .gs-psa-gdesc   { font-size: 2.4cqw; font-weight: 800; line-height: 1; }
    .gs-psa-grade   { font-size: 8.5cqw; font-weight: 900; line-height: 1; }
    .gs-psa-cert    { font-size: 2cqw;   font-weight: 600; line-height: 1; font-family: monospace; }
    /* BGS/Beckett */
    .gs-bgs-brand { font-size: 4cqw;   font-weight: 900; }
    .gs-bgs-tag   { font-size: 2.2cqw; }
    .gs-bgs-name  { font-size: 3.8cqw; font-weight: 900; }
    .gs-bgs-meta  { font-size: 2.2cqw; font-weight: 600; }
    .gs-bgs-sub   { font-size: 1.9cqw; font-weight: 600; }
    .gs-bgs-subv  { font-size: 2.2cqw; font-weight: 900; }
    .gs-bgs-grade { font-size: 8cqw;   font-weight: 900; line-height: 1; }
    .gs-bgs-gdesc { font-size: 1.9cqw; font-weight: 800; }
    .gs-bgs-cert  { font-size: 1.8cqw; font-weight: 600; }
    /* CGC / SGC */
    .gs-cgc-brand { font-size: 4cqw;   font-weight: 900; }
    .gs-cgc-tag   { font-size: 2.2cqw; }
    .gs-cgc-name  { font-size: 3.8cqw; font-weight: 900; }
    .gs-cgc-meta  { font-size: 2.2cqw; font-weight: 600; }
    .gs-cgc-grade { font-size: 8cqw;   font-weight: 900; line-height: 1; }
    .gs-cgc-gdesc { font-size: 2cqw;   font-weight: 700; }
    .gs-cgc-cert  { font-size: 1.8cqw; font-weight: 600; }
  `;
  document.head.appendChild(s);
}

// ─── PSA LABEL ─────────────────────────────────────────────────────────────────
// Real PSA slab layout (from reference photo):
//
//  ┌──────────[RED BORDER]──────────────────────────────────────┐
//  │  2000 POKEMON ROCKET                 │  #68               │
//  │  SQUIRTLE                            │  NM-MT             │
//  │  1ST EDITION                         │    8  (BIG RED)    │
//  │  ▐▌▐▐▌▐▌  [PSA oval]                 │  116960641         │
//  └────────────────────────────────────────────────────────────┘
//
// Left (65%): 3 text rows + bottom row with horizontal barcode + PSA logo
// Right (35%): card #, grade desc, LARGE grade number, cert — all stacked
const PSALabel: React.FC<{
  cardName:string; setName:string; number:string;
  grade:string; certNum:string; rarity?:string;
}> = ({ cardName, setName, number, grade, certNum, rarity }) => (
  <div className="gs-label-inner" style={{
    display:'flex', flexDirection:'row', alignItems:'stretch',
    background:'#FFFFFF',
    border:'2px solid #C8102E',
    borderRadius:'2px',
    fontFamily:'"Arial Narrow",Arial,Helvetica,sans-serif',
    overflow:'hidden',
  }}>

    {/* LEFT SECTION — 65% */}
    <div style={{
      width:'65%', flexShrink:0,
      display:'flex', flexDirection:'column', justifyContent:'space-between',
      padding:'5% 4% 4% 4%',
      borderRight:'1.5px solid #C8102E',
    }}>
      {/* Row 1: Set name */}
      <div className="gs-psa-setname" style={{ color:'#111', textTransform:'uppercase', letterSpacing:'0.03em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
        {setName}
      </div>
      {/* Row 2: Card name — largest text */}
      <div className="gs-psa-name" style={{ color:'#000', textTransform:'uppercase', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
        {cardName}
      </div>
      {/* Row 3: Edition/rarity */}
      <div className="gs-psa-edition" style={{ color:'#333', textTransform:'uppercase', letterSpacing:'0.03em' }}>
        {rarity || '1ST EDITION'}
      </div>
      {/* Row 4: Barcode + PSA logo side by side */}
      <div style={{ display:'flex', alignItems:'center', gap:'5%', marginTop:'2%' }}>
        {/* Horizontal barcode */}
        <div style={{
          flex:1, height:'5cqw', maxHeight:'100%',
          background:'repeating-linear-gradient(to right,#111 0px,#111 1.2px,transparent 1.2px,transparent 2.4px,#111 2.4px,#111 3.2px,transparent 3.2px,transparent 4.8px)',
          opacity:0.85,
        }} />
        {/* PSA blue oval logo */}
        <div style={{
          flexShrink:0,
          width:'18%',
          aspectRatio:'1.6/1',
          borderRadius:'30% 30% 45% 45% / 40% 40% 60% 60%',
          background:'linear-gradient(160deg,#1d58c0,#2565de,#163d90)',
          border:'0.5px solid #0f3080',
          boxShadow:'inset 0 1px 2px rgba(255,255,255,0.25)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <span className="gs-psa-logotext" style={{ color:'#fff', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif', letterSpacing:'0.02em' }}>PSA</span>
        </div>
      </div>
    </div>

    {/* RIGHT SECTION — 35% */}
    <div style={{
      width:'35%', flexShrink:0,
      display:'flex', flexDirection:'column', justifyContent:'space-between',
      alignItems:'flex-end',
      padding:'5% 5% 4% 4%',
    }}>
      {/* Card number */}
      <div className="gs-psa-cardnum" style={{ color:'#111' }}>#{number}</div>
      {/* Grade description */}
      <div className="gs-psa-gdesc" style={{ color:'#C8102E', textTransform:'uppercase' }}>
        {getGradeDescription('PSA', grade)}
      </div>
      {/* Large grade number */}
      <div className="gs-psa-grade" style={{ color:'#C8102E', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif' }}>
        {grade}
      </div>
      {/* Cert number */}
      <div className="gs-psa-cert" style={{ color:'#555' }}>{certNum}</div>
    </div>
  </div>
);

// ─── BECKETT / BGS LABEL ─────────────────────────────────────────────────────
const BeckettLabel: React.FC<{
  cardName:string; setName:string; number:string; grade:string; certNum:string;
  subgrades:{centering:string;corners:string;edges:string;surface:string};
  bgsLabelType:string;
}> = ({ cardName, setName, number, grade, certNum, subgrades, bgsLabelType }) => {
  type T = { bg:string; border:string; hBg:string; hFg:string; tFg:string; nFg:string; mFg:string; bBg:string; bBorder:string; gFg:string };
  const themes: Record<string,T> = {
    black: { bg:'linear-gradient(135deg,#0c0c0c,#1e1e1e,#0c0c0c)', border:'#D4AF37', hBg:'#000', hFg:'#D4AF37', tFg:'rgba(212,175,55,0.75)', nFg:'#fff', mFg:'rgba(212,175,55,0.85)', bBg:'#000', bBorder:'#D4AF37', gFg:'#D4AF37' },
    gold:  { bg:'linear-gradient(135deg,#C5A028,#EDD46A,#C09010,#F0D870)', border:'#8B6910', hBg:'#9A7800', hFg:'#fffae0', tFg:'rgba(255,250,200,0.9)', nFg:'#0d0600', mFg:'#4a3000', bBg:'#fffae0', bBorder:'#8B6910', gFg:'#3a2000' },
    silver:{ bg:'linear-gradient(135deg,#9BA0A3,#D4D6D8,#878C8F,#C0C3C5)', border:'#5a5f62', hBg:'#7a7f82', hFg:'#fff', tFg:'rgba(255,255,255,0.9)', nFg:'#000', mFg:'#222', bBg:'#fff', bBorder:'#5a5f62', gFg:'#111' },
    bronze:{ bg:'linear-gradient(135deg,#8B5E3C,#D09A6A,#7A4F2E,#BF9060)', border:'#5a3010', hBg:'#7a4a28', hFg:'#fff', tFg:'rgba(255,240,220,0.9)', nFg:'#0d0400', mFg:'#4a2000', bBg:'#fff8f0', bBorder:'#5a3010', gFg:'#3a1800' },
  };
  const t = themes[bgsLabelType] || themes.bronze;
  return (
    <div className="gs-label-inner" style={{ background:t.bg, border:`1.5px solid ${t.border}`, borderRadius:'2px', overflow:'hidden', fontFamily:'"Arial Narrow",Arial,Helvetica,sans-serif', display:'flex', flexDirection:'column' }}>
      {/* Header */}
      <div style={{ background:t.hBg, borderBottom:`1px solid ${t.border}`, padding:'3% 5%', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <span className="gs-bgs-brand" style={{ color:t.hFg, letterSpacing:'0.08em', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif', lineHeight:1, textTransform:'uppercase' }}>BECKETT</span>
        <span className="gs-bgs-tag" style={{ color:t.tFg, letterSpacing:'0.03em', textTransform:'uppercase' }}>GRADING SERVICES</span>
      </div>
      {/* Body */}
      <div style={{ flex:1, display:'flex', alignItems:'stretch', padding:'3% 4%', gap:'3%', overflow:'hidden' }}>
        <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'space-evenly', minWidth:0 }}>
          <div className="gs-bgs-name" style={{ color:t.nFg, textTransform:'uppercase', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.1 }}>{cardName}</div>
          <div className="gs-bgs-meta" style={{ color:t.mFg, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1 }}>{setName} #{number}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2% 6%' }}>
            {([['CTR',subgrades.centering],['COR',subgrades.corners],['EDG',subgrades.edges],['SUR',subgrades.surface]] as [string,string][]).map(([l,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
                <span className="gs-bgs-sub" style={{ color:t.mFg, textTransform:'uppercase' }}>{l}</span>
                <span className="gs-bgs-subv" style={{ color:t.nFg }}>{v}</span>
              </div>
            ))}
          </div>
          <div className="gs-bgs-cert" style={{ fontFamily:'monospace', color:t.mFg, lineHeight:1 }}>{certNum}</div>
        </div>
        {/* Grade box */}
        <div style={{ background:t.bBg, border:`1.5px solid ${t.bBorder}`, borderRadius:'3px', width:'24%', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2%' }}>
          <div className="gs-bgs-grade" style={{ color:t.gFg, fontFamily:'Impact,"Arial Narrow",Arial,sans-serif' }}>{grade}</div>
          <div style={{ width:'70%', height:'1px', background:t.bBorder, margin:'4% 0' }} />
          <div className="gs-bgs-gdesc" style={{ color:t.gFg, textTransform:'uppercase', textAlign:'center', lineHeight:1.2 }}>{getGradeDescription('BGS', grade)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── CGC LABEL ────────────────────────────────────────────────────────────────
const CGCLabel: React.FC<{
  cardName:string; setName:string; number:string; grade:string; certNum:string;
}> = ({ cardName, setName, number, grade, certNum }) => {
  const g = parseFloat(grade);
  const color = g>=10?'#7B2FBE':g>=9.5?'#00A8E8':g>=9?'#1565C0':g>=8?'#2E7D32':g>=6?'#F57F17':'#888';
  return (
    <div className="gs-label-inner" style={{ background:'#fff', border:`1.5px solid ${color}`, borderRadius:'2px', overflow:'hidden', fontFamily:'"Arial Narrow",Arial,Helvetica,sans-serif', display:'flex', flexDirection:'column' }}>
      <div style={{ background:color, padding:'3% 5%', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <span className="gs-cgc-brand" style={{ color:'#fff', letterSpacing:'0.08em', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif', lineHeight:1 }}>CGC</span>
        <span className="gs-cgc-tag" style={{ color:'rgba(255,255,255,0.85)', letterSpacing:'0.03em', textTransform:'uppercase' }}>TRADING CARDS</span>
        <div style={{ aspectRatio:'1', width:'8%', borderRadius:'50%', background:'conic-gradient(from 45deg,#E0C3FC,#8EC5FC,#A8E8FF,#FFD6FF,#E0C3FC)', border:'1px solid rgba(255,255,255,0.7)', flexShrink:0 }} />
      </div>
      <div style={{ flex:1, display:'flex', alignItems:'stretch', padding:'3% 4%', gap:'3%', overflow:'hidden' }}>
        <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'space-evenly', minWidth:0, borderRight:`1px solid ${color}44`, paddingRight:'3%' }}>
          <div className="gs-cgc-name" style={{ color:'#111', textTransform:'uppercase', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.1 }}>{cardName}</div>
          <div className="gs-cgc-meta" style={{ color:'#555', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1 }}>{setName} #{number}</div>
          <div className="gs-cgc-cert" style={{ fontFamily:'monospace', color:'#888', lineHeight:1 }}>{certNum}</div>
        </div>
        <div style={{ width:'24%', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div className="gs-cgc-grade" style={{ color, fontFamily:'Impact,"Arial Narrow",Arial,sans-serif' }}>{grade}</div>
          <div className="gs-cgc-gdesc" style={{ color, textTransform:'uppercase', textAlign:'center', lineHeight:1.2 }}>{getGradeDescription('CGC', grade)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── SGC / Generic ────────────────────────────────────────────────────────────
const SGCLabel: React.FC<{
  company:string; cardName:string; setName:string; number:string; grade:string; certNum:string;
}> = ({ company, cardName, setName, number, grade, certNum }) => (
  <div className="gs-label-inner" style={{ background:'#111', border:'1px solid #333', borderRadius:'2px', overflow:'hidden', fontFamily:'"Arial Narrow",Arial,Helvetica,sans-serif', display:'flex', flexDirection:'column' }}>
    <div style={{ background:'#000', borderBottom:'1px solid #333', padding:'3% 5%', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
      <span className="gs-cgc-brand" style={{ color:'#fff', letterSpacing:'0.08em', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif', lineHeight:1 }}>{company}</span>
      <span className="gs-cgc-tag" style={{ color:'#888', letterSpacing:'0.03em', textTransform:'uppercase' }}>AUTHENTICATED & GRADED</span>
    </div>
    <div style={{ flex:1, display:'flex', alignItems:'stretch', padding:'3% 4%', gap:'3%', overflow:'hidden' }}>
      <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'space-evenly', minWidth:0, borderRight:'1px solid #333', paddingRight:'3%' }}>
        <div className="gs-cgc-name" style={{ color:'#EEE', textTransform:'uppercase', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1.1 }}>{cardName}</div>
        <div className="gs-cgc-meta" style={{ color:'#999', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1 }}>{setName} #{number}</div>
        <div className="gs-cgc-cert" style={{ fontFamily:'monospace', color:'#666', lineHeight:1 }}>{certNum}</div>
      </div>
      <div style={{ width:'24%', flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <div className="gs-cgc-grade" style={{ color:'#4CAF50', fontFamily:'Impact,"Arial Narrow",Arial,sans-serif' }}>{grade}</div>
        <div className="gs-cgc-gdesc" style={{ color:'#4CAF50', textTransform:'uppercase', textAlign:'center', lineHeight:1.2 }}>{getGradeDescription(company, grade)}</div>
      </div>
    </div>
  </div>
);

// ─── MAIN SLAB ────────────────────────────────────────────────────────────────
const GradedSlab: React.FC<GradedSlabProps> = ({
  imageUrl, cardName, setName, number,
  gradingCompany = 'PSA', gradingGrade = '10',
  aspectRatio = '2.3/3.7', height = '100%', rarity,
}) => {
  const company = gradingCompany.toUpperCase();
  const grade = gradingGrade;
  const certNum = getCertNumber(company, cardName, number);
  const gradeVal = parseFloat(grade) || 10;
  const subgrades = getBeckettSubgrades(gradeVal, `${cardName}-${number}`);

  let bgsLabelType = 'bronze';
  if (gradeVal >= 9.5) {
    bgsLabelType = 'gold';
    if (gradeVal === 10 && Object.values(subgrades).every(v => v === '10')) bgsLabelType = 'black';
  } else if (gradeVal >= 8.5) bgsLabelType = 'silver';

  const accentColor =
    company === 'PSA' ? 'rgba(200,16,46,0.3)' :
    company === 'BGS' ? (bgsLabelType === 'black' ? 'rgba(212,175,55,0.5)' : 'rgba(160,145,40,0.35)') :
    company === 'CGC' ? 'rgba(0,168,232,0.3)' :
    'rgba(76,175,80,0.3)';

  // Inner card frame — PSA uses warm gold trim matching real slab photo
  const innerTrim =
    company === 'PSA' ? '2px solid rgba(205,160,35,0.5)' :
    company === 'BGS' ? (bgsLabelType === 'black' ? '2px solid rgba(212,175,55,0.4)' : '1.5px solid rgba(255,255,255,0.1)') :
    '1.5px solid rgba(255,255,255,0.12)';

  return (
    <div
      className="gs-slab"
      style={{
        width: '100%',
        height: typeof height === 'number' ? `${height}px` : height,
        aspectRatio,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Clear acrylic casing
        backgroundColor: 'rgba(215,228,242,0.07)',
        backdropFilter: 'blur(4px)',
        border: '4px solid rgba(200,215,232,0.26)',
        outline: `1px solid ${accentColor}`,
        borderRadius: '14px',
        boxShadow: `
          0 16px 40px rgba(0,0,0,0.65),
          0 4px 12px rgba(0,0,0,0.45),
          inset 0 0 0 1px rgba(255,255,255,0.3),
          inset 0 2px 6px rgba(255,255,255,0.16),
          inset 0 0 12px rgba(0,0,0,0.16)
        `,
        overflow: 'hidden',
        padding: '4%',
        boxSizing: 'border-box',
        justifyContent: 'flex-start',
        gap: 0,
      }}
    >
      {/* Glare shimmer */}
      <div style={{
        position:'absolute', top:'-120%', left:'-120%', width:'280%', height:'280%',
        pointerEvents:'none', zIndex:4,
        background:'linear-gradient(135deg,rgba(255,255,255,0.18) 0%,rgba(255,255,255,0.03) 40%,transparent 50%)',
        transform:'rotate(-40deg)', opacity:0.2,
      }} />
      {/* Inner bezel */}
      <div style={{
        position:'absolute', top:'2.5%', left:'2.5%', right:'2.5%', bottom:'2.5%',
        border:'1px solid rgba(255,255,255,0.09)', borderRadius:'10px',
        pointerEvents:'none', zIndex:3,
      }} />

      {/* ── Label: exactly 29cqw tall via .gs-label-wrap ── */}
      <div className="gs-label-wrap" style={{ position:'relative', zIndex:2 }}>
        {company === 'PSA' && <PSALabel cardName={cardName} setName={setName} number={number} grade={grade} certNum={certNum} rarity={rarity} />}
        {company === 'BGS' && <BeckettLabel cardName={cardName} setName={setName} number={number} grade={grade} certNum={certNum} subgrades={subgrades} bgsLabelType={bgsLabelType} />}
        {company === 'CGC' && <CGCLabel cardName={cardName} setName={setName} number={number} grade={grade} certNum={certNum} />}
        {company !== 'PSA' && company !== 'BGS' && company !== 'CGC' && <SGCLabel company={company} cardName={cardName} setName={setName} number={number} grade={grade} certNum={certNum} />}
      </div>

      {/* ── Plastic divider rib ── */}
      <div style={{
        height: '3cqw',
        minHeight: '2px',
        margin: '2.5cqw -4%',
        background: 'linear-gradient(to bottom,rgba(0,0,0,0.28) 0%,rgba(255,255,255,0.1) 50%,rgba(0,0,0,0.36) 100%)',
        borderTop: '1px solid rgba(255,255,255,0.14)',
        borderBottom: '1px solid rgba(0,0,0,0.36)',
        flexShrink: 0,
        zIndex: 2,
        position: 'relative',
      }} />

      {/* ── Card image: fills all remaining height (~78%) ── */}
      <div style={{
        flex: 1,
        border: innerTrim,
        borderRadius: '5%',
        backgroundColor: 'rgba(0,0,0,0.14)',
        boxShadow: 'inset 0 0 8px rgba(0,0,0,0.5)',
        padding: '2%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', position: 'relative', zIndex: 1,
      }}>
        <img
          src={imageUrl}
          alt={cardName}
          style={{
            width: '100%', height: '100%', objectFit: 'contain',
            borderRadius: '3%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        />
      </div>
    </div>
  );
};

export default GradedSlab;
